import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL;
if (!API_BASE) {
  throw new Error('API URL not configured');
}

// Formats date without timezone shift (prevents Dec 29 becoming Dec 28)
const formatDateSafe = (dateString) => {
  if (!dateString) return '-';
  const parts = dateString.split('T')[0].split('-');
  if (parts.length !== 3) return '-';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(parts[1]) - 1]} ${parseInt(parts[2])}, ${parts[0]}`;
};

// Helper: safely parse specialization whether stored as JSON array or plain text
const parseSpecializations = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : [String(parsed)];
      } catch {
        return [trimmed];
      }
    }
    return [trimmed];
  }
  return [String(value)];
};

const MemberManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [editFormData, setEditFormData] = useState({});
  const [createFormData, setCreateFormData] = useState({
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    address: '',
    postal_code: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    institution: '',
    job_title: '',
    experience_years: '',
    membership_type: 'professional',
    membership_status: 'pending',
    payment_status: 'pending'
  });
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, [currentPage]);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      if (!token) {
        toast.error('You must be logged in as an admin to view members.');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE}/admin/members?page=${currentPage}&limit=${itemsPerPage}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const membersList = response.data.data?.members || response.data.data || [];
        const pagination = response.data.data?.pagination || {};
        setMembers(membersList);
        setTotalMembers(pagination.total || membersList.length);
      } else {
        toast.error('Failed to load members');
      }
    } catch (error) {
      console.error('Error fetching members:', error);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view members.');
      } else {
        toast.error('Failed to load members. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewMember = (member) => {
    setSelectedMember(member);
    setShowModal(true);
  };

  const editMember = (member) => {
    setSelectedMember(member);
    setEditFormData({
      title: member.title || '',
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      phone: member.phone || '',
      country: member.country || '',
      city: member.city || '',
      address: member.address || '',
      postal_code: member.postal_code || '',
      date_of_birth: member.date_of_birth ? member.date_of_birth.split('T')[0] : '',
      gender: member.gender || '',
      nationality: member.nationality || '',
      institution: member.institution || '',
      job_title: member.job_title || '',
      experience_years: member.experience_years || '',
      membership_type: member.membership_type || '',
      payment_status: member.payment_status || ''
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.patch(
        `${API_BASE}/admin/members/${selectedMember.id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Member updated successfully!');
        setShowEditModal(false);
        fetchMembers();
      }
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error(error.response?.data?.message || 'Failed to update member');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (member) => {
    setSelectedMember(member);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setActionLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(
        `${API_BASE}/admin/members/${selectedMember.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Member deleted successfully!');
        setShowDeleteModal(false);
        fetchMembers();
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error(error.response?.data?.message || 'Failed to delete member');
    } finally {
      setActionLoading(false);
    }
  };

  const openStatusModal = (member) => {
    setSelectedMember(member);
    setNewStatus(member.membership_status);
    setStatusNotes('');
    setShowStatusModal(true);
  };

  const handleResendPasswordLink = async (member) => {
    if (!window.confirm(`Send password setup link to ${member.first_name} ${member.last_name} (${member.email})?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE}/admin/members/${member.id}/resend-password-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`Password setup link sent to ${member.email}`);
      } else {
        toast.error(response.data.message || 'Failed to send link');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send password setup link');
    }
  };

  const handleResetPassword = async (member, newPassword) => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters with uppercase, lowercase, number, and special character');
      return;
    }
    if (!window.confirm(`Reset password for ${member.first_name} ${member.last_name} (${member.email})?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE}/admin/members/${member.id}/reset-password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`Password reset for ${member.email}`);
      } else {
        toast.error(response.data.message || 'Failed to reset password');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleResetAccountPassword = async (member) => {
    if (!window.confirm(`Reset account password for ${member.first_name} ${member.last_name} (${member.email})?\n\nA temporary password will be generated and sent to their email. They will be required to change it on next login.`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE}/admin/members/${member.id}/reset-account-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const tempPass = response.data.data?.tempPassword;
        toast.success(`Account password reset for ${member.email}. Temp password: ${tempPass}`, { autoClose: 15000 });
      } else {
        toast.error(response.data.message || 'Failed to reset account password');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset account password');
    }
  };

  const handleStatusChange = async (e) => {
    e.preventDefault();
    setActionLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.patch(
        `${API_BASE}/admin/members/${selectedMember.id}/status`,
        { status: newStatus, notes: statusNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Member status updated to ${newStatus}!`);
        setShowStatusModal(false);
        fetchMembers();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const openCreateModal = () => {
    setCreateFormData({
      title: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      country: '',
      city: '',
      address: '',
      postal_code: '',
      date_of_birth: '',
      gender: '',
      nationality: '',
      institution: '',
      job_title: '',
      experience_years: '',
      membership_type: 'professional',
      membership_status: 'pending',
      payment_status: 'pending'
    });
    setShowCreateModal(true);
  };

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE}/admin/members`,
        createFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Member created successfully!');
        setShowCreateModal(false);
        fetchMembers();
      }
    } catch (error) {
      console.error('Error creating member:', error);
      toast.error(error.response?.data?.message || 'Failed to create member');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': 'bg-success',
      'pending': 'bg-warning text-dark',
      'expired': 'bg-danger',
      'suspended': 'bg-secondary'
    };
    return statusMap[status] || 'bg-secondary';
  };

  const getTypeBadge = (type) => {
    const typeMap = {
      'professional': 'bg-primary',
      'student': 'bg-info',
      'associate': 'bg-success',
      'international': 'bg-warning text-dark'
    };
    return typeMap[type] || 'bg-secondary';
  };

  const getFilteredMembers = () => {
    return members.filter(member => {
      const matchesSearch =
        `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.country?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = filterStatus === 'all' || member.membership_status === filterStatus;
      const matchesType = filterType === 'all' || member.membership_type === filterType;

      return matchesSearch && matchesStatus && matchesType;
    });
  };

  if (loading) {
    return (
      <div className="card shadow">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow">
      <div className="card-header bg-white py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="h4 mb-0 fw-bold">Member Management</h2>
            <p className="text-muted mb-0 small">Manage all ARCA members</p>
          </div>
          <button onClick={openCreateModal} className="btn btn-success">
            <i className="bi bi-plus-circle me-2"></i>
            Create New Member
          </button>
        </div>
      </div>

      <div className="card-body">
        {/* Filters and Items Per Page */}
        <div className="mb-4 row g-3">
          <div className="col-md-3">
            <input
              type="text"
              placeholder="Search by name, email, or country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
          <div className="col-md-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="form-select"
            >
              <option value="all">All Types</option>
              <option value="professional">Professional</option>
              <option value="student">Student</option>
              <option value="associate">Associate</option>
              <option value="international">International</option>
            </select>
          </div>
          <div className="col-md-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-select"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div className="col-md-3">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="form-select"
            >
              <option value="10">10 per page</option>
              <option value="20">20 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
              <option value="200">200 per page</option>
              <option value="500">500 per page</option>
              <option value="1000">1000 per page</option>
              <option value="10000">10000 per page</option>
            </select>
          </div>
        </div>

        {/* Members Table */}
        <div className="table-responsive">
          <table className="table table-hover">
            <thead className="table-light">
              <tr>
                <th>Member</th>
                <th>Email</th>
                <th>Country</th>
                <th>Type</th>
                <th>Status</th>
                <th>Institution</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredMembers().length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No members found
                  </td>
                </tr>
              ) : (
                getFilteredMembers().map((member) => (
                  <tr
                    key={member.id}
                    style={{
                      backgroundColor: member.membership_status === 'deleted' ? '#f8d7da' : 'transparent',
                      opacity: member.membership_status === 'deleted' ? 0.7 : 1,
                      color: member.membership_status === 'deleted' ? '#721c24' : 'inherit'
                    }}
                  >
                    <td>
                      <div className="d-flex align-items-center">
                        {member.profile_picture && (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${member.profile_picture}`}
                            alt={`${member.first_name} ${member.last_name}`}
                            className="rounded-circle me-2"
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'cover',
                              filter: member.membership_status === 'deleted' ? 'grayscale(100%)' : 'none'
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div>
                          <div className="fw-medium" style={{
                            textDecoration: member.membership_status === 'deleted' ? 'line-through' : 'none'
                          }}>
                            {member.title} {member.first_name} {member.last_name}
                          </div>
                          {member.member_number && (
                            <small className="text-muted">{member.member_number}</small>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-muted">{member.email}</td>
                    <td className="text-muted">{member.country}</td>
                    <td>
                      <span className={`badge ${getTypeBadge(member.membership_type)}`}>
                        {member.membership_type}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${member.membership_status === 'deleted' ? 'bg-danger' : getStatusBadge(member.membership_status)}`}>
                        {member.membership_status}
                      </span>
                    </td>
                    <td className="text-muted">{member.institution || '-'}</td>
                    <td className="text-muted">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap">
                        <button
                          onClick={() => viewMember(member)}
                          className="btn btn-sm btn-primary"
                        >
                          <i className="bi bi-eye me-1"></i>
                          View
                        </button>
                        <button
                          onClick={() => editMember(member)}
                          className="btn btn-sm btn-success"
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </button>
                        <button
                          onClick={() => openStatusModal(member)}
                          className="btn btn-sm btn-warning"
                        >
                          <i className="bi bi-arrow-repeat me-1"></i>
                          Change Status
                        </button>
                        {!member.password_hash && (
                          <button
                            onClick={() => handleResendPasswordLink(member)}
                            className="btn btn-sm btn-info text-white"
                            title="Send password setup link"
                          >
                            <i className="bi bi-envelope-arrow-up me-1"></i>
                            Send Password Link
                          </button>
                        )}
                        <button
                          onClick={() => {
                            const newPass = prompt(`Enter new password for ${member.first_name} ${member.last_name}:\n(Min 8 chars, uppercase, lowercase, number, special char)`);
                            if (newPass) handleResetPassword(member, newPass);
                          }}
                          className="btn btn-sm btn-secondary"
                          title="Manually set a new password for this member"
                        >
                          <i className="bi bi-key me-1"></i>
                          Reset Password
                        </button>
                        <button
                          onClick={() => handleResetAccountPassword(member)}
                          className="btn btn-sm btn-warning"
                          title="Generate temporary password and email to member. They must change it on next login."
                        >
                          <i className="bi bi-shield-lock me-1"></i>
                          Reset Account
                        </button>
                        <button
                          onClick={() => openDeleteModal(member)}
                          className="btn btn-sm btn-danger"
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination and Summary */}
        <div className="mt-3 d-flex justify-content-between align-items-center">
          <div className="text-muted small">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalMembers)} of {totalMembers} members
          </div>

          {totalMembers > itemsPerPage && (
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>

                {[...Array(Math.ceil(totalMembers / itemsPerPage))].map((_, index) => {
                  const pageNumber = index + 1;
                  // Show first 3 pages, last 3 pages, and pages around current page
                  if (
                    pageNumber === 1 ||
                    pageNumber === Math.ceil(totalMembers / itemsPerPage) ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      </li>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <li key={pageNumber} className="page-item disabled"><span className="page-link">...</span></li>;
                  }
                  return null;
                })}

                <li className={`page-item ${currentPage === Math.ceil(totalMembers / itemsPerPage) ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === Math.ceil(totalMembers / itemsPerPage)}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>

      {/* Create Member Modal */}
      {showCreateModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ maxHeight: '90vh' }}>
            <div className="modal-content">
              <div
                className="modal-header text-white border-0"
                style={{
                  background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)'
                }}
              >
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-person-plus-fill me-2"></i>
                  Create New Member
                </h5>
                <button onClick={() => setShowCreateModal(false)} className="btn-close btn-close-white" type="button" disabled={actionLoading}></button>
              </div>

              <form onSubmit={handleCreateSubmit}>
                <div className="modal-body" style={{ maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
                  <div className="row g-3">
                    {/* Personal Information */}
                    <div className="col-12">
                      <h5 className="border-bottom pb-2 fw-semibold" style={{ color: '#6B8E23' }}>
                        <i className="bi bi-person me-2"></i>
                        Personal Information
                      </h5>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Title</label>
                      <select name="title" value={createFormData.title} onChange={handleCreateChange} className="form-select">
                        <option value="">Select</option>
                        <option value="Mr.">Mr.</option>
                        <option value="Mrs.">Mrs.</option>
                        <option value="Ms.">Ms.</option>
                        <option value="Dr.">Dr.</option>
                        <option value="Prof.">Prof.</option>
                        <option value="RRT.">RRT.</option>
                        <option value="RCP.">RCP.</option>
                        <option value="RT.">RT.</option>
                        <option value="RN.">RN.</option>
                        <option value="CRNA">CRNA</option>
                        <option value="PT/DPT">PT/DPT</option>
                        <option value="OT/OTD">OT/OTD</option>
                        <option value="SLP">SLP</option>
                        <option value="PA/PA-C">PA/PA-C</option>
                        <option value="NP/APRN">NP/APRN</option>
                        <option value="DDS/DMD">DDS/DMD</option>
                        <option value="DVM">DVM</option>
                        <option value="PharmD">PharmD</option>
                        <option value="OD">OD</option>
                        <option value="LCSW">LCSW</option>
                        <option value="LPN">LPN</option>
                        <option value="LMT">LMT</option>
                        <option value="RD/RDN">RD/RDN</option>
                        <option value="DO">DO</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">First Name *</label>
                      <input type="text" name="first_name" value={createFormData.first_name} onChange={handleCreateChange} className="form-control" required />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label fw-semibold">Last Name *</label>
                      <input type="text" name="last_name" value={createFormData.last_name} onChange={handleCreateChange} className="form-control" required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Email *</label>
                      <input type="email" name="email" value={createFormData.email} onChange={handleCreateChange} className="form-control" required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Phone</label>
                      <input type="text" name="phone" value={createFormData.phone} onChange={handleCreateChange} className="form-control" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Date of Birth</label>
                      <input type="date" name="date_of_birth" value={createFormData.date_of_birth} onChange={handleCreateChange} className="form-control" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Gender</label>
                      <select name="gender" value={createFormData.gender} onChange={handleCreateChange} className="form-select">
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Nationality</label>
                      <input type="text" name="nationality" value={createFormData.nationality} onChange={handleCreateChange} className="form-control" />
                    </div>

                    {/* Location Information */}
                    <div className="col-12 mt-4">
                      <h5 className="border-bottom pb-2 fw-semibold" style={{ color: '#6B8E23' }}>
                        <i className="bi bi-geo-alt me-2"></i>
                        Location
                      </h5>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Country *</label>
                      <select name="country" value={createFormData.country} onChange={handleCreateChange} className="form-select" required>
                        <option value="">Select Country</option>
                        <option value="Afghanistan">Afghanistan</option>
                        <option value="Albania">Albania</option>
                        <option value="Algeria">Algeria</option>
                        <option value="Andorra">Andorra</option>
                        <option value="Angola">Angola</option>
                        <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                        <option value="Argentina">Argentina</option>
                        <option value="Armenia">Armenia</option>
                        <option value="Australia">Australia</option>
                        <option value="Austria">Austria</option>
                        <option value="Azerbaijan">Azerbaijan</option>
                        <option value="Bahamas">Bahamas</option>
                        <option value="Bahrain">Bahrain</option>
                        <option value="Bangladesh">Bangladesh</option>
                        <option value="Barbados">Barbados</option>
                        <option value="Belarus">Belarus</option>
                        <option value="Belgium">Belgium</option>
                        <option value="Belize">Belize</option>
                        <option value="Benin">Benin</option>
                        <option value="Bhutan">Bhutan</option>
                        <option value="Bolivia">Bolivia</option>
                        <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                        <option value="Botswana">Botswana</option>
                        <option value="Brazil">Brazil</option>
                        <option value="Brunei">Brunei</option>
                        <option value="Bulgaria">Bulgaria</option>
                        <option value="Burkina Faso">Burkina Faso</option>
                        <option value="Burundi">Burundi</option>
                        <option value="Cabo Verde">Cabo Verde</option>
                        <option value="Cambodia">Cambodia</option>
                        <option value="Cameroon">Cameroon</option>
                        <option value="Canada">Canada</option>
                        <option value="Central African Republic">Central African Republic</option>
                        <option value="Chad">Chad</option>
                        <option value="Chile">Chile</option>
                        <option value="China">China</option>
                        <option value="Colombia">Colombia</option>
                        <option value="Comoros">Comoros</option>
                        <option value="Congo (Congo-Brazzaville)">Congo (Congo-Brazzaville)</option>
                        <option value="Costa Rica">Costa Rica</option>
                        <option value="Croatia">Croatia</option>
                        <option value="Cuba">Cuba</option>
                        <option value="Cyprus">Cyprus</option>
                        <option value="Czechia (Czech Republic)">Czechia (Czech Republic)</option>
                        <option value="Democratic Republic of the Congo">Democratic Republic of the Congo</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Djibouti">Djibouti</option>
                        <option value="Dominica">Dominica</option>
                        <option value="Dominican Republic">Dominican Republic</option>
                        <option value="Ecuador">Ecuador</option>
                        <option value="Egypt">Egypt</option>
                        <option value="El Salvador">El Salvador</option>
                        <option value="Equatorial Guinea">Equatorial Guinea</option>
                        <option value="Eritrea">Eritrea</option>
                        <option value="Estonia">Estonia</option>
                        <option value="Eswatini">Eswatini</option>
                        <option value="Ethiopia">Ethiopia</option>
                        <option value="Fiji">Fiji</option>
                        <option value="Finland">Finland</option>
                        <option value="France">France</option>
                        <option value="Gabon">Gabon</option>
                        <option value="Gambia">Gambia</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Germany">Germany</option>
                        <option value="Ghana">Ghana</option>
                        <option value="Greece">Greece</option>
                        <option value="Grenada">Grenada</option>
                        <option value="Guatemala">Guatemala</option>
                        <option value="Guinea">Guinea</option>
                        <option value="Guinea-Bissau">Guinea-Bissau</option>
                        <option value="Guyana">Guyana</option>
                        <option value="Haiti">Haiti</option>
                        <option value="Holy See">Holy See</option>
                        <option value="Honduras">Honduras</option>
                        <option value="Hungary">Hungary</option>
                        <option value="Iceland">Iceland</option>
                        <option value="India">India</option>
                        <option value="Indonesia">Indonesia</option>
                        <option value="Iran">Iran</option>
                        <option value="Iraq">Iraq</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Israel">Israel</option>
                        <option value="Italy">Italy</option>
                        <option value="Jamaica">Jamaica</option>
                        <option value="Japan">Japan</option>
                        <option value="Jordan">Jordan</option>
                        <option value="Kazakhstan">Kazakhstan</option>
                        <option value="Kenya">Kenya</option>
                        <option value="Kiribati">Kiribati</option>
                        <option value="Kuwait">Kuwait</option>
                        <option value="Kyrgyzstan">Kyrgyzstan</option>
                        <option value="Laos">Laos</option>
                        <option value="Latvia">Latvia</option>
                        <option value="Lebanon">Lebanon</option>
                        <option value="Lesotho">Lesotho</option>
                        <option value="Liberia">Liberia</option>
                        <option value="Libya">Libya</option>
                        <option value="Liechtenstein">Liechtenstein</option>
                        <option value="Lithuania">Lithuania</option>
                        <option value="Luxembourg">Luxembourg</option>
                        <option value="Madagascar">Madagascar</option>
                        <option value="Malawi">Malawi</option>
                        <option value="Malaysia">Malaysia</option>
                        <option value="Maldives">Maldives</option>
                        <option value="Mali">Mali</option>
                        <option value="Malta">Malta</option>
                        <option value="Marshall Islands">Marshall Islands</option>
                        <option value="Mauritania">Mauritania</option>
                        <option value="Mauritius">Mauritius</option>
                        <option value="Mexico">Mexico</option>
                        <option value="Micronesia">Micronesia</option>
                        <option value="Moldova">Moldova</option>
                        <option value="Monaco">Monaco</option>
                        <option value="Mongolia">Mongolia</option>
                        <option value="Montenegro">Montenegro</option>
                        <option value="Morocco">Morocco</option>
                        <option value="Mozambique">Mozambique</option>
                        <option value="Myanmar (formerly Burma)">Myanmar (formerly Burma)</option>
                        <option value="Namibia">Namibia</option>
                        <option value="Nauru">Nauru</option>
                        <option value="Nepal">Nepal</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="New Zealand">New Zealand</option>
                        <option value="Nicaragua">Nicaragua</option>
                        <option value="Niger">Niger</option>
                        <option value="Nigeria">Nigeria</option>
                        <option value="North Korea">North Korea</option>
                        <option value="North Macedonia">North Macedonia</option>
                        <option value="Norway">Norway</option>
                        <option value="Oman">Oman</option>
                        <option value="Pakistan">Pakistan</option>
                        <option value="Palau">Palau</option>
                        <option value="Palestine State">Palestine State</option>
                        <option value="Panama">Panama</option>
                        <option value="Papua New Guinea">Papua New Guinea</option>
                        <option value="Paraguay">Paraguay</option>
                        <option value="Peru">Peru</option>
                        <option value="Philippines">Philippines</option>
                        <option value="Poland">Poland</option>
                        <option value="Portugal">Portugal</option>
                        <option value="Qatar">Qatar</option>
                        <option value="Romania">Romania</option>
                        <option value="Russia">Russia</option>
                        <option value="Rwanda">Rwanda</option>
                        <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                        <option value="Saint Lucia">Saint Lucia</option>
                        <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                        <option value="Samoa">Samoa</option>
                        <option value="San Marino">San Marino</option>
                        <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Senegal">Senegal</option>
                        <option value="Serbia">Serbia</option>
                        <option value="Seychelles">Seychelles</option>
                        <option value="Sierra Leone">Sierra Leone</option>
                        <option value="Singapore">Singapore</option>
                        <option value="Slovakia">Slovakia</option>
                        <option value="Slovenia">Slovenia</option>
                        <option value="Solomon Islands">Solomon Islands</option>
                        <option value="Somalia">Somalia</option>
                        <option value="South Africa">South Africa</option>
                        <option value="South Korea">South Korea</option>
                        <option value="South Sudan">South Sudan</option>
                        <option value="Spain">Spain</option>
                        <option value="Sri Lanka">Sri Lanka</option>
                        <option value="Sudan">Sudan</option>
                        <option value="Suriname">Suriname</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Switzerland">Switzerland</option>
                        <option value="Syria">Syria</option>
                        <option value="Tajikistan">Tajikistan</option>
                        <option value="Tanzania">Tanzania</option>
                        <option value="Thailand">Thailand</option>
                        <option value="Timor-Leste">Timor-Leste</option>
                        <option value="Togo">Togo</option>
                        <option value="Tonga">Tonga</option>
                        <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                        <option value="Tunisia">Tunisia</option>
                        <option value="Turkey">Turkey</option>
                        <option value="Turkmenistan">Turkmenistan</option>
                        <option value="Tuvalu">Tuvalu</option>
                        <option value="Uganda">Uganda</option>
                        <option value="Ukraine">Ukraine</option>
                        <option value="United Arab Emirates">United Arab Emirates</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="United States of America">United States of America</option>
                        <option value="Uruguay">Uruguay</option>
                        <option value="Uzbekistan">Uzbekistan</option>
                        <option value="Vanuatu">Vanuatu</option>
                        <option value="Venezuela">Venezuela</option>
                        <option value="Vietnam">Vietnam</option>
                        <option value="Yemen">Yemen</option>
                        <option value="Zambia">Zambia</option>
                        <option value="Zimbabwe">Zimbabwe</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">City</label>
                      <input type="text" name="city" value={createFormData.city} onChange={handleCreateChange} className="form-control" />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label fw-semibold">Address</label>
                      <input type="text" name="address" value={createFormData.address} onChange={handleCreateChange} className="form-control" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Postal Code</label>
                      <input type="text" name="postal_code" value={createFormData.postal_code} onChange={handleCreateChange} className="form-control" />
                    </div>

                    {/* Professional Information */}
                    <div className="col-12 mt-4">
                      <h5 className="border-bottom pb-2 fw-semibold" style={{ color: '#6B8E23' }}>
                        <i className="bi bi-briefcase me-2"></i>
                        Professional Information
                      </h5>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Institution</label>
                      <input type="text" name="institution" value={createFormData.institution} onChange={handleCreateChange} className="form-control" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Job Title</label>
                      <input type="text" name="job_title" value={createFormData.job_title} onChange={handleCreateChange} className="form-control" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Experience (Years)</label>
                      <input type="number" name="experience_years" value={createFormData.experience_years} onChange={handleCreateChange} className="form-control" min="0" />
                    </div>

                    {/* Membership Information */}
                    <div className="col-12 mt-4">
                      <h5 className="border-bottom pb-2 fw-semibold" style={{ color: '#6B8E23' }}>
                        <i className="bi bi-card-checklist me-2"></i>
                        Membership Information
                      </h5>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Membership Type *</label>
                      <select name="membership_type" value={createFormData.membership_type} onChange={handleCreateChange} className="form-select" required>
                        <option value="professional">Professional Member</option>
                        <option value="student">Student Member</option>
                        <option value="associate">Associate Member</option>
                        <option value="international">International Member</option>
                        <option value="vendor">Vendor Member</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Status *</label>
                      <select name="membership_status" value={createFormData.membership_status} onChange={handleCreateChange} className="form-select" required>
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Payment Status</label>
                      <select name="payment_status" value={createFormData.payment_status} onChange={handleCreateChange} className="form-select">
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="modal-footer bg-light border-0">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary" disabled={actionLoading}>
                    <i className="bi bi-x-circle me-2"></i>
                    Cancel
                  </button>
                  <button type="submit" className="btn text-white fw-semibold" disabled={actionLoading} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
                    {actionLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Create Member
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditModal && selectedMember && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ maxHeight: '90vh' }}>
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="bi bi-pencil-square me-2"></i>
                  Edit Member
                </h5>
                <button onClick={() => setShowEditModal(false)} className="btn-close btn-close-white" type="button" disabled={actionLoading}></button>
              </div>

              <form onSubmit={handleEditSubmit}>
                <div className="modal-body" style={{ maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
                  <div className="row g-3">
                    {/* Personal Information */}
                    <div className="col-12">
                      <h5 className="border-bottom pb-2 text-success">
                        <i className="bi bi-person me-2"></i>
                        Personal Information
                      </h5>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Title</label>
                      <select name="title" value={editFormData.title} onChange={handleEditChange} className="form-select">
                        <option value="">Select</option>
                        <option value="Mr">Mr</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Ms">Ms</option>
                        <option value="Dr">Dr</option>
                        <option value="Prof">Prof</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">First Name *</label>
                      <input type="text" name="first_name" value={editFormData.first_name} onChange={handleEditChange} className="form-control" required />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label fw-semibold">Last Name *</label>
                      <input type="text" name="last_name" value={editFormData.last_name} onChange={handleEditChange} className="form-control" required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Email *</label>
                      <input type="email" name="email" value={editFormData.email} onChange={handleEditChange} className="form-control" required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Phone</label>
                      <input type="text" name="phone" value={editFormData.phone} onChange={handleEditChange} className="form-control" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Date of Birth</label>
                      <input type="date" name="date_of_birth" value={editFormData.date_of_birth} onChange={handleEditChange} className="form-control" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Gender</label>
                      <select name="gender" value={editFormData.gender} onChange={handleEditChange} className="form-select">
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Nationality</label>
                      <input type="text" name="nationality" value={editFormData.nationality} onChange={handleEditChange} className="form-control" />
                    </div>

                    {/* Location Information */}
                    <div className="col-12 mt-4">
                      <h5 className="border-bottom pb-2 text-success">
                        <i className="bi bi-geo-alt me-2"></i>
                        Location
                      </h5>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Country *</label>
                      <select name="country" value={editFormData.country} onChange={handleEditChange} className="form-select" required>
                        <option value="">Select Country</option>
                        <option value="Afghanistan">Afghanistan</option>
                        <option value="Albania">Albania</option>
                        <option value="Algeria">Algeria</option>
                        <option value="Andorra">Andorra</option>
                        <option value="Angola">Angola</option>
                        <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                        <option value="Argentina">Argentina</option>
                        <option value="Armenia">Armenia</option>
                        <option value="Australia">Australia</option>
                        <option value="Austria">Austria</option>
                        <option value="Azerbaijan">Azerbaijan</option>
                        <option value="Bahamas">Bahamas</option>
                        <option value="Bahrain">Bahrain</option>
                        <option value="Bangladesh">Bangladesh</option>
                        <option value="Barbados">Barbados</option>
                        <option value="Belarus">Belarus</option>
                        <option value="Belgium">Belgium</option>
                        <option value="Belize">Belize</option>
                        <option value="Benin">Benin</option>
                        <option value="Bhutan">Bhutan</option>
                        <option value="Bolivia">Bolivia</option>
                        <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                        <option value="Botswana">Botswana</option>
                        <option value="Brazil">Brazil</option>
                        <option value="Brunei">Brunei</option>
                        <option value="Bulgaria">Bulgaria</option>
                        <option value="Burkina Faso">Burkina Faso</option>
                        <option value="Burundi">Burundi</option>
                        <option value="Cabo Verde">Cabo Verde</option>
                        <option value="Cambodia">Cambodia</option>
                        <option value="Cameroon">Cameroon</option>
                        <option value="Canada">Canada</option>
                        <option value="Central African Republic">Central African Republic</option>
                        <option value="Chad">Chad</option>
                        <option value="Chile">Chile</option>
                        <option value="China">China</option>
                        <option value="Colombia">Colombia</option>
                        <option value="Comoros">Comoros</option>
                        <option value="Congo (Congo-Brazzaville)">Congo (Congo-Brazzaville)</option>
                        <option value="Costa Rica">Costa Rica</option>
                        <option value="Croatia">Croatia</option>
                        <option value="Cuba">Cuba</option>
                        <option value="Cyprus">Cyprus</option>
                        <option value="Czechia (Czech Republic)">Czechia (Czech Republic)</option>
                        <option value="Democratic Republic of the Congo">Democratic Republic of the Congo</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Djibouti">Djibouti</option>
                        <option value="Dominica">Dominica</option>
                        <option value="Dominican Republic">Dominican Republic</option>
                        <option value="Ecuador">Ecuador</option>
                        <option value="Egypt">Egypt</option>
                        <option value="El Salvador">El Salvador</option>
                        <option value="Equatorial Guinea">Equatorial Guinea</option>
                        <option value="Eritrea">Eritrea</option>
                        <option value="Estonia">Estonia</option>
                        <option value="Eswatini">Eswatini</option>
                        <option value="Ethiopia">Ethiopia</option>
                        <option value="Fiji">Fiji</option>
                        <option value="Finland">Finland</option>
                        <option value="France">France</option>
                        <option value="Gabon">Gabon</option>
                        <option value="Gambia">Gambia</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Germany">Germany</option>
                        <option value="Ghana">Ghana</option>
                        <option value="Greece">Greece</option>
                        <option value="Grenada">Grenada</option>
                        <option value="Guatemala">Guatemala</option>
                        <option value="Guinea">Guinea</option>
                        <option value="Guinea-Bissau">Guinea-Bissau</option>
                        <option value="Guyana">Guyana</option>
                        <option value="Haiti">Haiti</option>
                        <option value="Holy See">Holy See</option>
                        <option value="Honduras">Honduras</option>
                        <option value="Hungary">Hungary</option>
                        <option value="Iceland">Iceland</option>
                        <option value="India">India</option>
                        <option value="Indonesia">Indonesia</option>
                        <option value="Iran">Iran</option>
                        <option value="Iraq">Iraq</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Israel">Israel</option>
                        <option value="Italy">Italy</option>
                        <option value="Jamaica">Jamaica</option>
                        <option value="Japan">Japan</option>
                        <option value="Jordan">Jordan</option>
                        <option value="Kazakhstan">Kazakhstan</option>
                        <option value="Kenya">Kenya</option>
                        <option value="Kiribati">Kiribati</option>
                        <option value="Kuwait">Kuwait</option>
                        <option value="Kyrgyzstan">Kyrgyzstan</option>
                        <option value="Laos">Laos</option>
                        <option value="Latvia">Latvia</option>
                        <option value="Lebanon">Lebanon</option>
                        <option value="Lesotho">Lesotho</option>
                        <option value="Liberia">Liberia</option>
                        <option value="Libya">Libya</option>
                        <option value="Liechtenstein">Liechtenstein</option>
                        <option value="Lithuania">Lithuania</option>
                        <option value="Luxembourg">Luxembourg</option>
                        <option value="Madagascar">Madagascar</option>
                        <option value="Malawi">Malawi</option>
                        <option value="Malaysia">Malaysia</option>
                        <option value="Maldives">Maldives</option>
                        <option value="Mali">Mali</option>
                        <option value="Malta">Malta</option>
                        <option value="Marshall Islands">Marshall Islands</option>
                        <option value="Mauritania">Mauritania</option>
                        <option value="Mauritius">Mauritius</option>
                        <option value="Mexico">Mexico</option>
                        <option value="Micronesia">Micronesia</option>
                        <option value="Moldova">Moldova</option>
                        <option value="Monaco">Monaco</option>
                        <option value="Mongolia">Mongolia</option>
                        <option value="Montenegro">Montenegro</option>
                        <option value="Morocco">Morocco</option>
                        <option value="Mozambique">Mozambique</option>
                        <option value="Myanmar (formerly Burma)">Myanmar (formerly Burma)</option>
                        <option value="Namibia">Namibia</option>
                        <option value="Nauru">Nauru</option>
                        <option value="Nepal">Nepal</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="New Zealand">New Zealand</option>
                        <option value="Nicaragua">Nicaragua</option>
                        <option value="Niger">Niger</option>
                        <option value="Nigeria">Nigeria</option>
                        <option value="North Korea">North Korea</option>
                        <option value="North Macedonia">North Macedonia</option>
                        <option value="Norway">Norway</option>
                        <option value="Oman">Oman</option>
                        <option value="Pakistan">Pakistan</option>
                        <option value="Palau">Palau</option>
                        <option value="Palestine State">Palestine State</option>
                        <option value="Panama">Panama</option>
                        <option value="Papua New Guinea">Papua New Guinea</option>
                        <option value="Paraguay">Paraguay</option>
                        <option value="Peru">Peru</option>
                        <option value="Philippines">Philippines</option>
                        <option value="Poland">Poland</option>
                        <option value="Portugal">Portugal</option>
                        <option value="Qatar">Qatar</option>
                        <option value="Romania">Romania</option>
                        <option value="Russia">Russia</option>
                        <option value="Rwanda">Rwanda</option>
                        <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                        <option value="Saint Lucia">Saint Lucia</option>
                        <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                        <option value="Samoa">Samoa</option>
                        <option value="San Marino">San Marino</option>
                        <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Senegal">Senegal</option>
                        <option value="Serbia">Serbia</option>
                        <option value="Seychelles">Seychelles</option>
                        <option value="Sierra Leone">Sierra Leone</option>
                        <option value="Singapore">Singapore</option>
                        <option value="Slovakia">Slovakia</option>
                        <option value="Slovenia">Slovenia</option>
                        <option value="Solomon Islands">Solomon Islands</option>
                        <option value="Somalia">Somalia</option>
                        <option value="South Africa">South Africa</option>
                        <option value="South Korea">South Korea</option>
                        <option value="South Sudan">South Sudan</option>
                        <option value="Spain">Spain</option>
                        <option value="Sri Lanka">Sri Lanka</option>
                        <option value="Sudan">Sudan</option>
                        <option value="Suriname">Suriname</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Switzerland">Switzerland</option>
                        <option value="Syria">Syria</option>
                        <option value="Tajikistan">Tajikistan</option>
                        <option value="Tanzania">Tanzania</option>
                        <option value="Thailand">Thailand</option>
                        <option value="Timor-Leste">Timor-Leste</option>
                        <option value="Togo">Togo</option>
                        <option value="Tonga">Tonga</option>
                        <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                        <option value="Tunisia">Tunisia</option>
                        <option value="Turkey">Turkey</option>
                        <option value="Turkmenistan">Turkmenistan</option>
                        <option value="Tuvalu">Tuvalu</option>
                        <option value="Uganda">Uganda</option>
                        <option value="Ukraine">Ukraine</option>
                        <option value="United Arab Emirates">United Arab Emirates</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="United States of America">United States of America</option>
                        <option value="Uruguay">Uruguay</option>
                        <option value="Uzbekistan">Uzbekistan</option>
                        <option value="Vanuatu">Vanuatu</option>
                        <option value="Venezuela">Venezuela</option>
                        <option value="Vietnam">Vietnam</option>
                        <option value="Yemen">Yemen</option>
                        <option value="Zambia">Zambia</option>
                        <option value="Zimbabwe">Zimbabwe</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">City</label>
                      <input type="text" name="city" value={editFormData.city} onChange={handleEditChange} className="form-control" />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label fw-semibold">Address</label>
                      <input type="text" name="address" value={editFormData.address} onChange={handleEditChange} className="form-control" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Postal Code</label>
                      <input type="text" name="postal_code" value={editFormData.postal_code} onChange={handleEditChange} className="form-control" />
                    </div>

                    {/* Professional Information */}
                    <div className="col-12 mt-4">
                      <h5 className="border-bottom pb-2 text-success">
                        <i className="bi bi-briefcase me-2"></i>
                        Professional Information
                      </h5>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Institution</label>
                      <input type="text" name="institution" value={editFormData.institution} onChange={handleEditChange} className="form-control" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Job Title</label>
                      <input type="text" name="job_title" value={editFormData.job_title} onChange={handleEditChange} className="form-control" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Experience (Years)</label>
                      <input type="number" name="experience_years" value={editFormData.experience_years} onChange={handleEditChange} className="form-control" min="0" />
                    </div>

                    {/* Membership Information */}
                    <div className="col-12 mt-4">
                      <h5 className="border-bottom pb-2 text-success">
                        <i className="bi bi-card-checklist me-2"></i>
                        Membership Information
                      </h5>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Membership Type *</label>
                      <select name="membership_type" value={editFormData.membership_type} onChange={handleEditChange} className="form-select" required>
                        <option value="professional">Professional</option>
                        <option value="student">Student</option>
                        <option value="associate">Associate</option>
                        <option value="international">International</option>
                        <option value="vendor">Vendor</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Payment Status</label>
                      <select name="payment_status" value={editFormData.payment_status} onChange={handleEditChange} className="form-select">
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="modal-footer bg-light">
                  <button type="button" onClick={() => setShowEditModal(false)} className="btn btn-secondary" disabled={actionLoading}>
                    <i className="bi bi-x-circle me-2"></i>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success" disabled={actionLoading} style={{ backgroundColor: '#28a745' }}>
                    {actionLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Update Member
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedMember && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">Confirm Delete</h5>
                <button onClick={() => setShowDeleteModal(false)} className="btn-close btn-close-white" type="button" disabled={actionLoading}></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Are you sure you want to delete this member?
                </p>
                <div className="bg-light p-3 rounded">
                  <strong>{selectedMember.title} {selectedMember.first_name} {selectedMember.last_name}</strong>
                  <br />
                  <small className="text-muted">{selectedMember.email}</small>
                </div>
                <div className="alert alert-warning mt-3 mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  This action will mark the member as deleted. This cannot be undone.
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowDeleteModal(false)} className="btn btn-secondary" disabled={actionLoading}>
                  Cancel
                </button>
                <button onClick={handleDelete} className="btn btn-danger" disabled={actionLoading}>
                  {actionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : 'Delete Member'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Status Modal */}
      {showStatusModal && selectedMember && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Member Status</h5>
                <button onClick={() => setShowStatusModal(false)} className="btn-close" type="button" disabled={actionLoading}></button>
              </div>
              <form onSubmit={handleStatusChange}>
                <div className="modal-body">
                  <div className="mb-3">
                    <strong>Member:</strong> {selectedMember.first_name} {selectedMember.last_name}
                    <br />
                    <small className="text-muted">{selectedMember.email}</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Current Status:</label>
                    <div>
                      <span className={`badge ${getStatusBadge(selectedMember.membership_status)}`}>
                        {selectedMember.membership_status}
                      </span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">New Status *</label>
                    <select
                      className="form-select"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      required
                    >
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="suspended">Suspended</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Notes (Optional)</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={statusNotes}
                      onChange={(e) => setStatusNotes(e.target.value)}
                      placeholder="Add any notes about this status change..."
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" onClick={() => setShowStatusModal(false)} className="btn btn-secondary" disabled={actionLoading}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                    {actionLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : 'Update Status'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Member Modal */}
      {showModal && selectedMember && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h3 className="modal-title">Member Details</h3>
                <button onClick={() => setShowModal(false)} className="btn-close" type="button"></button>
              </div>

              <div className="modal-body">
                <div className="row g-4">
                  {/* Personal Information */}
                  <div className="col-12">
                    <div className="bg-light p-3 rounded">
                      <h4 className="fw-semibold mb-3">Personal Information</h4>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Full Name</label>
                          <p className="mb-0">{selectedMember.title} {selectedMember.first_name} {selectedMember.last_name}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Member Number</label>
                          <p className="mb-0">{selectedMember.member_number || 'Not assigned'}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Email</label>
                          <p className="mb-0">{selectedMember.email}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Phone</label>
                          <p className="mb-0">{selectedMember.phone || '-'}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Date of Birth</label>
                          <p className="mb-0">{formatDateSafe(selectedMember.date_of_birth)}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Gender</label>
                          <p className="mb-0">{selectedMember.gender || '-'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Information */}
                  <div className="col-12">
                    <div className="bg-light p-3 rounded">
                      <h4 className="fw-semibold mb-3">Location</h4>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Country</label>
                          <p className="mb-0">{selectedMember.country}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">City</label>
                          <p className="mb-0">{selectedMember.city || '-'}</p>
                        </div>
                        <div className="col-md-12">
                          <label className="form-label fw-medium">Address</label>
                          <p className="mb-0">{selectedMember.address || '-'}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Postal Code</label>
                          <p className="mb-0">{selectedMember.postal_code || '-'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Professional Information */}
                  <div className="col-12">
                    <div className="bg-light p-3 rounded">
                      <h4 className="fw-semibold mb-3">Professional Information</h4>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Institution</label>
                          <p className="mb-0">{selectedMember.institution || '-'}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Job Title</label>
                          <p className="mb-0">{selectedMember.job_title || '-'}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Experience Years</label>
                          <p className="mb-0">{selectedMember.experience_years || '0'} years</p>
                        </div>
                        <div className="col-md-12">
                          <label className="form-label fw-medium">Specialization</label>
                          <div>
                            {selectedMember.specialization && parseSpecializations(selectedMember.specialization).map((spec, index) => (
                              <span key={index} className="badge bg-primary me-2 mb-2">
                                {spec}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Membership Information */}
                  <div className="col-12">
                    <div className="bg-light p-3 rounded">
                      <h4 className="fw-semibold mb-3">Membership Information</h4>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label fw-medium">Type</label>
                          <p className="mb-0">
                            <span className={`badge ${getTypeBadge(selectedMember.membership_type)}`}>
                              {selectedMember.membership_type}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-medium">Status</label>
                          <p className="mb-0">
                            <span className={`badge ${getStatusBadge(selectedMember.membership_status)}`}>
                              {selectedMember.membership_status}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-medium">Payment Status</label>
                          <p className="mb-0">
                            <span className={`badge ${selectedMember.payment_status === 'paid' ? 'bg-success' : 'bg-warning text-dark'}`}>
                              {selectedMember.payment_status}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">Start Date</label>
                          <p className="mb-0">{formatDateSafe(selectedMember.membership_start_date)}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-medium">End Date</label>
                          <p className="mb-0">{formatDateSafe(selectedMember.membership_end_date)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* System Information */}
                  <div className="col-12">
                    <div className="bg-light p-3 rounded">
                      <h4 className="fw-semibold mb-3">System Information</h4>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label fw-medium">Email Verified</label>
                          <p className="mb-0">
                            <span className={`badge ${selectedMember.email_verified ? 'bg-success' : 'bg-warning text-dark'}`}>
                              {selectedMember.email_verified ? 'Yes' : 'No'}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-medium">Created At</label>
                          <p className="mb-0">{new Date(selectedMember.created_at).toLocaleString()}</p>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-medium">Last Updated</label>
                          <p className="mb-0">{new Date(selectedMember.updated_at).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button onClick={() => setShowModal(false)} className="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberManagement;
